import { useTheme, themeColors } from '../ThemeContext';
import { booths, Booth } from '../../../mocks/boothData';
import { useTranslation } from 'react-i18next';

interface ProgressScreenProps {
  boothStates: Record<string, Booth['status']>;
  onBoothSelect: (booth: Booth) => void;
}

const HALLS = ['Hall A', 'Hall B', 'Hall C', 'Hall D'];

const ProgressScreen = ({ boothStates, onBoothSelect }: ProgressScreenProps) => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();

  const allWithState = booths.map(b => ({ ...b, status: boothStates[b.id] || b.status }));
  const visited = allWithState.filter(b => b.status === 'visited');
  const important = allWithState.filter(b => b.status === 'important');
  const notVisited = allWithState.filter(b => b.status === 'not-visited' || b.status === 'recommended');
  const pct = Math.round((visited.length / booths.length) * 100);

  const hallStats = HALLS.map(hall => {
    const hallBooths = allWithState.filter(b => b.hall === hall);
    const hallVisited = hallBooths.filter(b => b.status === 'visited');
    return { hall, total: hallBooths.length, visited: hallVisited.length, pct: hallBooths.length ? Math.round((hallVisited.length / hallBooths.length) * 100) : 0 };
  });

  const hallGradients = {
    dark: { 'Hall A': '#00d4ff', 'Hall B': '#a78bfa', 'Hall C': '#34d399', 'Hall D': '#fb923c' },
    light: { 'Hall A': '#0070E0', 'Hall B': '#7C3AED', 'Hall C': '#059669', 'Hall D': '#D97706' },
  };

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg}`}>
      <div className={`sticky top-0 z-20 ${c.navBg} backdrop-blur-xl border-b ${c.navBorder} px-5 pt-12 pb-4`}>
        <h1 className={`text-xl font-black ${c.text}`}>{t('progress.title')}</h1>
        <p className={`text-xs ${c.textMuted}`}>{t('progress.subtitle')}</p>
      </div>

      <div className="px-5 pt-5 flex flex-col gap-5">
        {/* Big ring */}
        <div className={`p-5 rounded-2xl border ${c.bgCard} ${c.border} flex flex-col items-center`}>
          <div className="relative w-36 h-36 mb-4">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" fill="none" stroke={isDark ? '#1e2d47' : '#e5e7eb'} strokeWidth="10" />
              <circle cx="60" cy="60" r="50" fill="none" stroke={isDark ? 'url(#grad)' : '#0070E0'} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 50}`} strokeDashoffset={`${2 * Math.PI * 50 * (1 - pct / 100)}`} className="transition-all duration-700" />
              <defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#00d4ff" /><stop offset="100%" stopColor="#a78bfa" /></linearGradient></defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-3xl font-black ${c.text}`}>{pct}%</span>
              <span className={`text-[11px] ${c.textMuted}`}>{t('progress.explored')}</span>
            </div>
          </div>
          <p className={`text-sm font-bold ${c.text} mb-1`}>{t('progress.coverage')}</p>
          <p className={`text-xs ${c.textMuted}`}>{visited.length} / {booths.length} {t('progress.visitedOf')}</p>
          <div className="grid grid-cols-3 gap-4 mt-4 w-full">
            {[
              { val: visited.length, label: t('progress.stat.visited'), color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', icon: 'ri-checkbox-circle-line' },
              { val: important.length, label: t('progress.stat.important'), color: isDark ? 'text-amber-400' : 'text-amber-600', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50', icon: 'ri-star-line' },
              { val: notVisited.length, label: t('progress.stat.remaining'), color: isDark ? 'text-[#00d4ff]' : 'text-blue-600', bg: isDark ? 'bg-[#00d4ff]/10' : 'bg-blue-50', icon: 'ri-map-pin-line' },
            ].map(s => (
              <div key={s.label} className={`flex flex-col items-center gap-1 p-2.5 rounded-xl ${s.bg}`}>
                <i className={`${s.icon} text-lg ${s.color}`}></i>
                <span className={`text-xl font-black ${c.text}`}>{s.val}</span>
                <span className={`text-[10px] ${c.textMuted}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Hall */}
        <div>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>{t('progress.byHall')}</p>
          <div className="flex flex-col gap-3">
            {hallStats.map(h => {
              const hColor = hallGradients[theme][h.hall as keyof typeof hallGradients.dark];
              return (
                <div key={h.hall} className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-sm" style={{ background: hColor }} />
                      <span className={`text-sm font-bold ${c.text}`}>{h.hall}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${c.textMuted}`}>{h.visited}/{h.total}</span>
                      <span className="text-sm font-black" style={{ color: hColor }}>{h.pct}%</span>
                    </div>
                  </div>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-[#1e2d47]' : 'bg-gray-100'}`}>
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${h.pct}%`, background: hColor }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Missed */}
        <div>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-error-warning-line mr-1 text-amber-400"></i>{t('progress.missedTitle')}
          </p>
          <div className="flex flex-col gap-3">
            {allWithState.filter(b => b.aiScore >= 85 && b.status !== 'visited').slice(0, 4).map(booth => (
              <button key={booth.id} onClick={() => onBoothSelect(booth)} className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all active:scale-[0.98] cursor-pointer ${c.bgCard} ${c.border}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${isDark ? 'bg-amber-500/20' : 'bg-amber-50'}`}>
                  <i className={`ri-building-2-line ${isDark ? 'text-amber-400' : 'text-amber-600'}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`text-sm font-bold ${c.text} truncate`}>{booth.company}</h3>
                  <p className={`text-[11px] ${c.textMuted}`}>{booth.boothNumber} • AI: {booth.aiScore}%</p>
                </div>
                <i className={`ri-arrow-right-line text-sm ${c.textMuted}`}></i>
              </button>
            ))}
          </div>
        </div>

        {/* Passed before */}
        <div>
          <p className={`text-xs font-bold uppercase tracking-widest ${c.textMuted} mb-3`}>
            <i className="ri-eye-line mr-1 text-sky-400"></i>{t('progress.passedTitle')}
          </p>
          <div className={`p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
            <div className="flex flex-col gap-2">
              {allWithState.filter(b => b.status === 'visited').map(booth => (
                <div key={booth.id} className="flex items-center gap-2">
                  <i className="ri-footprint-line text-xs text-emerald-400"></i>
                  <span className={`text-xs ${c.textSecondary} flex-1`}>{booth.company}</span>
                  <span className={`text-[10px] ${c.textMuted}`}>{booth.boothNumber}</span>
                </div>
              ))}
              {visited.length === 0 && <p className={`text-xs ${c.textMuted} text-center py-4`}>{t('progress.noVisited')}</p>}
            </div>
          </div>
        </div>

        {/* Analytics */}
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: 'ri-time-line', val: '2h 34m', label: t('progress.time'), color: isDark ? 'text-[#00d4ff]' : 'text-blue-600', bg: isDark ? 'bg-[#00d4ff]/10' : 'bg-blue-50' },
            { icon: 'ri-footprint-line', val: '4.2 km', label: t('progress.distance'), color: isDark ? 'text-[#a78bfa]' : 'text-purple-600', bg: isDark ? 'bg-[#a78bfa]/10' : 'bg-purple-50' },
            { icon: 'ri-handshake-line', val: '6', label: t('progress.connections'), color: isDark ? 'text-emerald-400' : 'text-emerald-600', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50' },
            { icon: 'ri-sticky-note-line', val: '4', label: t('progress.notes'), color: isDark ? 'text-amber-400' : 'text-amber-600', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50' },
          ].map(stat => (
            <div key={stat.label} className={`flex items-center gap-3 p-4 rounded-2xl border ${c.bgCard} ${c.border}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${stat.bg}`}>
                <i className={`${stat.icon} text-lg ${stat.color}`}></i>
              </div>
              <div>
                <p className={`text-lg font-black ${c.text}`}>{stat.val}</p>
                <p className={`text-[10px] ${c.textMuted}`}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressScreen;
